import type { InterviewTemplate, AssessmentListItemForUser } from '@/services/api';
import { AssessmentStatus } from '@/services/api';
import { HtsAdminFollowUpStatus, HtsSubmissionStatus } from '@evaltick/shared';
import { AnswerMode } from '@/store/interviewStore';
import { formatDateTime } from '@/utils/formatters';

export type PartKey = 'hts' | 'comp';
export type PartStatus = 'new' | 'prog' | 'done';

export interface TemplatePart {
  key: PartKey;
  label: string;
  short: string;
  qs: number;
  status: PartStatus;
  done: number;
}

/** Same 3-state vocabulary as a single part — complete only once every part is. */
export type TemplateStatus = PartStatus;

/** Maps an Assessment history row back to its owning template, for the one
 * signal InterviewTemplate itself doesn't expose: whether a Competency
 * attempt is currently in progress (only "ever completed" is a bulk field).
 * HTS needs no such lookup — htsSubmissionStatus already covers it. */
export type AssessmentsByTemplate = Map<string, AssessmentListItemForUser[]>;

export function buildAssessmentsByTemplate(
  assessments: AssessmentListItemForUser[]
): AssessmentsByTemplate {
  const map: AssessmentsByTemplate = new Map();
  for (const a of assessments) {
    const arr = map.get(a.interviewTemplate.id) ?? [];
    arr.push(a);
    map.set(a.interviewTemplate.id, arr);
  }
  return map;
}

/** The one or two questionnaires a template has, with real per-part status —
 * mirrors the reference concept's partsOf, using only fields the API actually
 * returns (see the Sessions redesign gap report for what's deliberately not
 * fetched in bulk here: the answered-so-far count, left at 0 until a
 * targeted fetch enriches the *selected* template's part — see SetupWizard). */
export function partsOf(
  template: InterviewTemplate,
  assessmentsByTemplate: AssessmentsByTemplate
): TemplatePart[] {
  const parts: TemplatePart[] = [];
  if (template.htsRequired) {
    const status: PartStatus =
      template.htsSubmissionStatus === HtsSubmissionStatus.DRAFT
        ? 'prog'
        : template.htsEverSubmitted
          ? 'done'
          : 'new';
    parts.push({
      key: 'hts',
      label: 'HTS Questionnaire',
      short: 'HTS',
      qs: template.htsQuestionCount ?? 0,
      status,
      done: 0,
    });
  }
  const compRows = assessmentsByTemplate.get(template.id) ?? [];
  const compInProgress = compRows.some((a) => a.status === AssessmentStatus.InProgress);
  const compStatus: PartStatus = compInProgress
    ? 'prog'
    : template.assessmentEverCompleted
      ? 'done'
      : 'new';
  parts.push({
    key: 'comp',
    label: 'Competency Questionnaire',
    short: 'Competency',
    qs: template.items.length,
    status: compStatus,
    done: 0,
  });
  return parts;
}

/** Interview-level status: done only when every part is; in progress if any
 * part has been started; otherwise not started. */
export function templateStatus(
  template: InterviewTemplate,
  assessmentsByTemplate: AssessmentsByTemplate
): TemplateStatus {
  const parts = partsOf(template, assessmentsByTemplate);
  if (parts.every((p) => p.status === 'done')) return 'done';
  if (parts.some((p) => p.status !== 'new')) return 'prog';
  return 'new';
}

export const openParts = (parts: TemplatePart[]): TemplatePart[] =>
  parts.filter((p) => p.status !== 'done');

export const canAttemptPart = (isGuest: boolean, status: PartStatus): boolean =>
  !isGuest || status !== 'done';

/** Only `htsRequired` decides the step path — the rest of the template is irrelevant here. */
type StepTemplate = Pick<InterviewTemplate, 'htsRequired'>;

export function needsWhich(template: StepTemplate): boolean {
  return template.htsRequired;
}

export function autoSelectPart(template: StepTemplate): PartKey | null {
  if (needsWhich(template)) return null;
  return 'comp';
}

export type StepKey = 'pick' | 'which' | 'questions' | 'mode' | 'preview';

export const STEP_TITLES: Record<StepKey, string> = {
  pick: 'Select interview',
  which: 'Where to start',
  questions: 'Choose competencies',
  mode: 'Answer mode',
  preview: 'Review',
};

/** Whether the Competency part gets the question picker: never for guests
 * (they sit every question), and not when resuming (scope is already fixed). */
export const canChooseQuestions = (isGuest: boolean, compStatus: PartStatus): boolean =>
  !isGuest && compStatus !== 'prog';

/** The computed step path — 3 to 5 long depending on choices. Always starts
 * at 'pick'; 'which' only when needed (see needsWhich); 'questions' (see
 * canChooseQuestions) and 'mode' only for Competency, as HTS is typed-only;
 * always ends at 'preview' once a template is selected. */
export function computeSteps(
  template: StepTemplate | null,
  part: PartKey | null,
  includePick: boolean = true,
  includeQuestions: boolean = false
): StepKey[] {
  const out: StepKey[] = includePick ? ['pick'] : [];
  if (template) {
    if (needsWhich(template)) out.push('which');
    if (part === 'comp') {
      if (includeQuestions) out.push('questions');
      out.push('mode');
    }
    out.push('preview');
  }
  return out;
}

/** Answer mode a freshly chosen part starts with: Competency defaults to chat,
 * HTS has no mode at all. */
export const defaultModeFor = (part: PartKey | null): AnswerMode | null =>
  part === 'comp' ? AnswerMode.Text : null;

export interface WizardEntry {
  templateId: string;
  part: PartKey | null;
  mode: AnswerMode | null;
  step: number;
}

/** Where the setup card lands when a template is (re)loaded into it.
 * With no part, it opens on "Where to start" so both questionnaires and their
 * status are in view (or on "Select interview" for a Competency-only template,
 * which has no such step). With a part, it skips to that part's first
 * remaining choice: answer mode for Competency, Review for HTS.
 *
 * The index ignores the question picker: it sits right before answer mode,
 * so where the wizard shows it, the same index lands on the picker instead. */
export function wizardEntryFor(
  template: Pick<InterviewTemplate, 'id' | 'htsRequired'>,
  part: PartKey | null
): WizardEntry {
  const resolvedPart = part ?? autoSelectPart(template);
  const steps = computeSteps(template, resolvedPart);
  const target: StepKey = part === null ? 'which' : part === 'comp' ? 'mode' : 'preview';
  return {
    templateId: template.id,
    part: resolvedPart,
    mode: defaultModeFor(resolvedPart),
    step: Math.max(0, steps.indexOf(target)),
  };
}

/** The other questionnaire on this template, if it still has work left —
 * what the "what's next" prompt offers once `finished` is done. */
export function remainingPart(parts: TemplatePart[], finished: PartKey): TemplatePart | null {
  return parts.find((p) => p.key !== finished && p.status !== 'done') ?? null;
}

/** HTS is a one-shot questionnaire: once submitted it can't be reopened
 * (HtsQuestionnaire bounces a SUBMITTED submission straight back to Sessions). */
export const isLocked = (part: TemplatePart): boolean => part.key === 'hts' && part.status === 'done';

export function partStatusText(part: TemplatePart): string {
  if (part.status === 'done') return part.key === 'hts' ? 'Submitted' : 'Completed';
  if (part.status === 'prog') {
    return part.qs > 0 && part.done > 0 ? `In progress · ${part.done} of ${part.qs}` : 'In progress';
  }
  return `${part.qs} question${part.qs === 1 ? '' : 's'}`;
}

/** "How feedback works" lines for the Review step — set before the candidate
 * starts, because the common assumption is that you get a score per question
 * and can redo one on the spot, when scores (and Try Again) only arrive once
 * every question in the attempt is answered. Competency lines are the
 * client's wording. */
export function feedbackNotes(
  partKey: PartKey,
  coachingEnabled: boolean,
  canRetake = true,
  // A guest never sees their results — they go to the agency that issued
  // the code. Kept separate from canRetake: the two happen to coincide at
  // the only call site today, but aren't the same thing.
  isGuest = false
): string[] {
  if (partKey === 'hts') {
    return [
      'You can save and come back as often as you like before submitting.',
      isGuest
        ? 'You submit the questionnaire once, and your results are forwarded to the agency that sent you the code.'
        : // Never "right after": a mandatory review trigger can hold the summary back.
          'You submit the questionnaire once, and your Readiness Summary appears once any required review is complete.',
    ];
  }
  const notes = [
    'Use the STARR format for behavioural-based questions and the CARL format for situational questions. Aim to speak for 5–7 minutes per answer.',
  ];
  // Guests get per-question feedback too when coaching is on — only scores
  // and Results are withheld from them. Coaching-off packages have none.
  if (coachingEnabled) {
    notes.push('Written and verbal feedback is provided after you complete each question.');
  }
  if (isGuest) {
    return [...notes, 'Once you’ve completed all questions, your results are forwarded to the agency that sent you the code.'];
  }
  notes.push('Once you’ve completed all questions, your scores will appear in Results.');
  if (canRetake) {
    notes.push('From Results, select Try Again to redo any questions you want to improve. Prioritize your lower-scoring answers first.');
  }
  return notes;
}

/** Detail line and button label for the questionnaire the "what's next"
 * prompt offers. */
export function remainingPartCopy(part: TemplatePart): { detail: string; primaryLabel: string } {
  if (part.status === 'prog') {
    return { detail: 'In progress. Pick up where you left off.', primaryLabel: `Resume ${part.short}` };
  }
  const qs = `${part.qs} question${part.qs === 1 ? '' : 's'}`;
  return { detail: `${qs}, not started yet`, primaryLabel: `Start ${part.short}` };
}

export interface GuestFollowUpBanner {
  type: 'info' | 'warning';
  message: string;
  description: string;
  canReply: boolean;
}

const FOLLOW_UP_CHECK_BACK_MS = 48 * 60 * 60 * 1000;

/**
 * The banner a guest sees once their HTS is submitted and the admin follow-up round is still open.
 * Null once answered (or with no submitted HTS); the page keeps its existing thank-you there. Info
 * rather than success while open, since green reads as "all done". `loginEndsAt` keeps the copy
 * from promising time past the guest's re-login window.
 */
export function guestFollowUpBanner(
  status: HtsAdminFollowUpStatus | null,
  competencyDone: boolean,
  loginEndsAt: string | null,
  now: Date = new Date()
): GuestFollowUpBanner | null {
  const endsAt = loginEndsAt ? new Date(loginEndsAt) : null;
  switch (status) {
    case HtsAdminFollowUpStatus.NotSent: {
      const checkBack =
        endsAt && endsAt.getTime() - now.getTime() < FOLLOW_UP_CHECK_BACK_MS
          ? `before ${formatDateTime(endsAt)}`
          : 'within the next 48 hours';
      return {
        type: 'info',
        message: competencyDone
          ? 'Thank you for completing your assessment.'
          : 'Thank you for completing your HTS assessment.',
        description: `An assessor is reviewing your answers and may have a few follow-up questions. Please log back in with the same access code ${checkBack} to check.`,
        canReply: false,
      };
    }
    case HtsAdminFollowUpStatus.AwaitingCandidate:
      return {
        type: 'warning',
        message: 'Follow-up questions received',
        description: `The assessor has a few follow-up questions for you. Please answer them${
          endsAt ? ` before ${formatDateTime(endsAt)}` : ''
        } so your results can be finalized.`,
        canReply: true,
      };
    default:
      return null;
  }
}
